import React from 'react'
import T from 'prop-types'
import ErrorPage from 'next/error'
import { useRouter } from 'next/router'
import { groq } from 'next-sanity'

import { resourcesQuery, IMAGE_FIELDS } from '../api'
import { getClient } from '../api/sanity'
import SEO from '../components/SEO'
import Article from '../components/Article'
import NavList from '../components/NavList'
import ResourceEntry from '../components/ResourceEntry'
import FileEntry from '../components/FileEntry'
import Section from '../components/Section'
import Button from '../components/Button'
import YouTube from '../components/YouTube'
import SanityPreview from '../components/SanityPreview'
import useColor from '../hooks/useColor'
import { useFocusSimple as useFocus } from '../hooks/useFocus'

const query = groq`{
  "resourcesPage": *[_type == "resources"][0]${resourcesQuery},
  "resources": *[_type=="resource"]{
    ...,
    file{
      asset->{
        extension,
        mimeType,
        size,
        url
      }
    },
    image${IMAGE_FIELDS}
  }|order(title asc)
}`

function InnerResourcesIndexPage({ data }) {
  const resourcesPage = data?.resourcesPage
  let menu = []

  if (resourcesPage) {
    if (
      resourcesPage.staticPagesData?.staticPages &&
      resourcesPage.staticPagesData.staticPages.length > 0
    ) {
      menu.push({ _key: 'staticPages', title: 'Sider' })
    }
  }

  menu = [
    ...menu,
    { _key: 'articles', title: 'Artikler' },
    { _key: 'externalArticles', title: 'Andre artikler' },
    { _key: 'videos', title: 'Videoer' },
    { _key: 'externalResources', title: 'Eksterne ressurser' },
    { _key: 'resources', title: 'Hjelpemidler' },
    { _key: 'ordbok', title: 'Ordbok' },
  ]

  const active = useFocus(menu.map(({ _key }) => _key))

  return (
    <>
      <SEO title="Ressurser" description={resourcesPage?.preamble} path="ressurser" />

      <NavList active={active} inline items={menu} back={{ title: 'Hjem', href: '/' }} />

      <Article title="Ressurser" preamble={resourcesPage?.preamble} type="page">
        {resourcesPage?.staticPagesData &&
          resourcesPage.staticPagesData.staticPages &&
          resourcesPage.staticPagesData.staticPages.length > 0 && (
            <ResourceEntry
              id="staticPages"
              title={resourcesPage.staticPagesData.title || 'Sider fra Tenk.'}
              preamble={resourcesPage.staticPagesData.preamble}
              items={resourcesPage.staticPagesData.staticPages}
            />
          )}
        {data?.resourcesPage?.articlesData && (
          <ResourceEntry
            id="articles"
            title={resourcesPage.articlesData.title || 'Artikler fra Tenk.'}
            preamble={resourcesPage.articlesData.preamble}
            items={resourcesPage.articlesData.articles}
          />
        )}
        {data?.resourcesPage?.externalArticlesData && (
          <ResourceEntry
            id="externalArticles"
            title={resourcesPage?.externalArticlesData.title || 'Andre artikler'}
            preamble={resourcesPage.externalArticlesData.preamble}
            items={resourcesPage.externalArticlesData.externalArticles}
          />
        )}
        {resourcesPage.videosData && (
          <Section
            id="videos"
            type="grid"
            title={resourcesPage.videosData.title || 'Videoer'}
            spaced
            preamble={
              <>
                <div style={{ marginBottom: '1.2em' }}>{resourcesPage.videosData.preamble}</div>
                <Button
                  label="Tenk. på YouTube ↗"
                  href="https://www.youtube.com/playlist?list=PLpD4dgsbRrdffihGnKAUtiqboZQNNRJcd"
                />
              </>
            }
          >
            {resourcesPage.videosData.videos?.map((item) => <YouTube key={item.id} {...item} />) ??
              null}
          </Section>
        )}

        {data?.resourcesPage?.externalResourcesData && (
          <ResourceEntry
            id="externalResources"
            title={resourcesPage.externalResourcesData.title || 'Eksterne ressurser'}
            preamble={resourcesPage.externalResourcesData.preamble}
            items={resourcesPage.externalResourcesData.externalResources}
            large
          />
        )}

        <FileEntry
          id="resources"
          title={data?.resourcesPage?.resourcesData?.title || 'Hjelpemidler'}
          preamble={data?.resourcesPage?.resourcesData?.preamble}
          items={data.resources}
        />

        <Section
          id="ordbok"
          type="grid"
          title={data?.resourcesPage?.dictionaryData?.title || 'Ordbok'}
          spaced
          preamble={
            <>
              <div style={{ marginBottom: '1.2em' }}>
                {data?.resourcesPage?.dictionaryData?.preamble}
              </div>
              <Button label="Ordbok" icon="chevron" href="/ordbok" />
            </>
          }
        />
      </Article>
    </>
  )
}

export default function ResourcesIndexPage({ preview, pageData, error, status }) {
  const router = useRouter()
  useColor(null, status)

  if (!router.isFallback && !pageData) {
    return <ErrorPage statusCode={404} />
  }

  if (error) {
    return <ErrorPage statusCode={error.statusCode} title={error.title} />
  }

  return (
    <SanityPreview query={query} initialData={pageData} preview={preview}>
      {(data) => {
        return <InnerResourcesIndexPage data={data} />
      }}
    </SanityPreview>
  )
}

ResourcesIndexPage.propTypes = {
  pageData: T.object.isRequired,
  status: T.string,
  preview: T.bool.isRequired,
  error: T.shape({
    statusCode: T.number.isRequired,
    title: T.string.isRequired,
    purpose: T.string,
  }),
}

export async function getStaticProps({ preview = false }) {
  const pageData = await getClient(preview).fetch(query)

  return {
    props: {
      preview,
      pageData,
    },
    revalidate: 10, // Revalidate at most every 10th second
  }
}
